import { CsvFileIssue } from '@/features/csvImport/admin/utils/checkCsvFile';
import { RLearningJourneyTeamImportFileErrorCode, RLearningJourneyTeamImportRowOutcome } from '@/store/v2';

/** Rows per page in the review tables. */
export const JOURNEY_TEAMS_CSV_IMPORT_PAGE_SIZE = 10;

/** The cap on a single upload, quoted back to the admin in the error. */
export const JOURNEY_TEAMS_CSV_IMPORT_MAX_ROWS = 5000;

export const JOURNEY_TEAMS_CSV_IMPORT_TITLE = 'Upload file';

export const JOURNEY_TEAMS_CSV_IMPORT_SUBTITLE = 'Set up learning teams from a file';

export const JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_FILE_NAME = 'learning_teams_population_template.csv';

/** Name of the CSV of rows that block set-up, offered on the review step. */
export const JOURNEY_TEAMS_CSV_IMPORT_BLOCKING_FILE_NAME = 'errors-and-missing-teams.csv';

/**
 * The columns a file has to carry, in this order. The server checks the header against the same
 * list and answers TEMPLATE_MISMATCH when it differs.
 */
export const JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_COLUMNS = ['Email', 'Team'];

/**
 * The whole template, held here rather than fetched — it is one header line. Inlined as a data
 * URI so the link needs no object-URL lifecycle and no network at all; paired with a `download`
 * attribute it saves the file rather than navigating. Same approach as the other two bulk flows,
 * where the CDN copy turned out to be unroutable.
 */
export const JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(
  `${JOURNEY_TEAMS_CSV_IMPORT_TEMPLATE_COLUMNS.join(',')}\n`,
)}`;

/**
 * What the admin is told when the whole file is refused. Newlines are significant — the snackbar
 * keeps them, matching the two-line messages in the design.
 */
export const JOURNEY_TEAMS_CSV_IMPORT_FILE_ERROR_MESSAGE: Record<RLearningJourneyTeamImportFileErrorCode, string> = {
  [RLearningJourneyTeamImportFileErrorCode.InvalidCsv]: 'Something went wrong.\nPlease try again.',
  [RLearningJourneyTeamImportFileErrorCode.TemplateMismatch]:
    "Your file doesn't match the template.\nDownload the template and try again.",
  [RLearningJourneyTeamImportFileErrorCode.TooLarge]: `Your file has too many rows.\nUpload up to ${JOURNEY_TEAMS_CSV_IMPORT_MAX_ROWS} at a time.`,
  [RLearningJourneyTeamImportFileErrorCode.NotCsv]: 'This file is not a CSV file. Upload a CSV file to continue.',
};

/**
 * The checks run in the browser answer in their own vocabulary; the admin is told about them in
 * the server's, so a file turned down before it is uploaded reads exactly like one the server
 * refuses. `NOT_CSV` has no entry here — the drop area turns those down on the extension alone,
 * before `checkCsvFile` ever looks inside.
 */
export const JOURNEY_TEAMS_CSV_IMPORT_FILE_ISSUE_ERROR_CODE: Record<CsvFileIssue, RLearningJourneyTeamImportFileErrorCode> = {
  invalidCsv: RLearningJourneyTeamImportFileErrorCode.InvalidCsv,
  templateMismatch: RLearningJourneyTeamImportFileErrorCode.TemplateMismatch,
  tooLarge: RLearningJourneyTeamImportFileErrorCode.TooLarge,
};

export const JOURNEY_TEAMS_CSV_IMPORT_CONFIRM_ERROR_MESSAGE =
  'Something went wrong while setting up learning teams.\nPlease try again later.';

/**
 * Raised on the Learning Teams page the admin lands on, so it names what was created rather than
 * repeating what they just reviewed.
 */
export const getJourneyTeamsCsvImportStartedMessage = (teamsCreated: number): string =>
  `${teamsCreated} learning ${teamsCreated === 1 ? 'team' : 'teams'} created and all journey learners added to a team.`;

/**
 * Reason shown against a rejected row. Every error outcome carries one: the three of them share a
 * single Errors bucket, whose label explains none of them on its own.
 */
export const JOURNEY_TEAMS_CSV_IMPORT_ROW_REASON: Partial<Record<RLearningJourneyTeamImportRowOutcome, string>> = {
  [RLearningJourneyTeamImportRowOutcome.InvalidEmail]: 'Invalid email format',
  [RLearningJourneyTeamImportRowOutcome.DuplicateInFile]: 'Duplicate email in file',
  [RLearningJourneyTeamImportRowOutcome.MissingRequiredField]: 'Missing required field',
};

/** Outcomes the Errors bucket merges, in the order the reason column should list them. */
export const JOURNEY_TEAMS_CSV_IMPORT_ERROR_OUTCOMES = [
  RLearningJourneyTeamImportRowOutcome.InvalidEmail,
  RLearningJourneyTeamImportRowOutcome.DuplicateInFile,
  RLearningJourneyTeamImportRowOutcome.MissingRequiredField,
];

/** Reason recorded against a journey learner whom the file leaves without a team. */
export const JOURNEY_TEAMS_CSV_IMPORT_MISSING_LEARNER_REASON = 'Missing a team';
